import * as React from 'react';
import { parseCoachingMessage } from '../../voice-agent/utils/parseCoachingMessage';
import { clearCachedConversationId, getCachedConversationId, setCachedConversationId } from './conversationCache';
import { CoachMessage, CoachMessageRole, PendingKeywordTag } from './types';
import { CoachUpstreamMessage, useRealtimeCoaching } from './useRealtimeCoaching';

/**
 * Arguments for `useCoaching`. All fields are optional because the hook waits
 * for a `companyId` before bootstrapping; passing only `companyId` is enough
 * to start (or resume) a conversation for that lead.
 */
export type UseCoachingArgs = {
	companyId?: string;
	contactId?: string;
	contactName?: string;
	contactTitle?: string;
	phoneCallId?: string;
	leadServedId?: string;
	/**
	 * Optional AI agent template id. Threaded through to
	 * `useRealtimeCoaching.createNew` so the conversation is created against
	 * that template. Only applied to fresh conversations — the bootstrap dedup
	 * key is intentionally NOT widened to include `agentTemplateId`, matching
	 * the legacy queue's behavior (switching templates mid-session takes effect
	 * on the next `startNewConversation`).
	 */
	agentTemplateId?: string;
};

export type UseCoachingApi = {
	messages: CoachMessage[];
	isResponding: boolean;
	isLoadingHistory: boolean;
	/** True if the active conversation was resumed from cache rather than freshly created. */
	wasResumed: boolean;
	/** Stable id for the active conversation, or null while bootstrapping. */
	conversationId: string | null;
	/**
	 * True while the bootstrap `createNew` POST is in flight. Exposed so the
	 * pre-dial gate (`waitForCoachingConversation`) can distinguish "still
	 * creating, keep waiting" from "create failed / never started" — mirrors
	 * the legacy queue's `coaching.isCreatingConversation` check in
	 * `CallButton`/`startCoachingBeforeCall`.
	 */
	isCreatingConversation: boolean;
	/**
	 * Send a composer message to the coach. When `tag` is provided, the next
	 * incoming assistant message is decorated with `pinnedKeyword` + `pinColor`
	 * so the UI can render the pinned-keyword ribbon.
	 */
	send(text: string, tag?: PendingKeywordTag): void;
	/** Append a finalized transcript entry to the conversation message stream. */
	addTranscriptEntry(entry: { id: string; speaker: string; text: string; timestamp: Date }): void;
	/**
	 * Enqueue an "ask the AI for a suggestion based on recent transcript"
	 * dispatch. Goes through the same send-queue as direct sends so it never
	 * overlaps an in-flight stream. No-op if no transcript has accumulated
	 * since the last suggestion / resume.
	 */
	requestAutoSuggestion(): void;
	/**
	 * Manually request a coaching suggestion based on accumulated transcript.
	 * Alias of {@link requestAutoSuggestion} surfaced for the "Get suggestion"
	 * button rendered when `coffeePreferences.autoSuggestionsEnabled === false`.
	 * Same queue + dedup semantics — at most one suggestion can be queued at a
	 * time.
	 */
	requestSuggestion(): void;
	/**
	 * Manually request the end-of-call summary. Mirrors {@link endCall} but
	 * skips the "transcript-required" guard — the user has explicitly clicked
	 * "Get summary" so we trust their intent. Routes through the same send
	 * queue so a concurrent in-flight stream (Game Plan, last suggestion) is
	 * allowed to finish first; deduped against any already-queued summary.
	 */
	requestPostCallSummary(): void;
	/**
	 * Drop the cached conversation for this lead and bootstrap a brand-new one.
	 * The next render of `useCoaching` will see no cached id and call
	 * `realtime.createNew` again, which re-fires the `before_call` keyword pins
	 * and clears the message stream. Use sparingly — this is the "start fresh"
	 * escape hatch from the center column header.
	 */
	startNewConversation(): void;
	/** Request the end-of-call summary and tear down the session. */
	endCall(): void;
};

const resolveRole = (message: CoachUpstreamMessage): CoachMessageRole => {
	if (message.role === 'assistant') return 'assistant';
	if (message.messageType === 'transcript') return 'transcript';
	return 'user';
};

const formatTime = (timestamp: Date): string =>
	timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const toCoachMessage = (message: CoachUpstreamMessage, tagForId: Map<string, PendingKeywordTag>): CoachMessage => {
	const role = resolveRole(message);
	const base: CoachMessage = {
		id: message.id,
		role,
		time: formatTime(message.timestamp),
		text: message.text,
		isStreaming: message.isStreaming || undefined,
	};

	if (role === 'assistant') {
		const parsed = parseCoachingMessage(message.text);
		if (parsed?.suggestion) base.sayThis = parsed.suggestion;
		if (parsed?.reasoning) base.why = parsed.reasoning;

		const tag = tagForId.get(message.id);
		if (tag) {
			base.pinnedKeyword = tag.pinnedKeyword;
			base.pinColor = tag.pinColor;
		}
	}

	return base;
};

/**
 * Focused-view coaching adapter. Owns conversation lifecycle (create / resume
 * / cache), keyword-tag claim queue, send-queue gate, and the upstream→view
 * message normalization. Wraps `useRealtimeCoaching` (the focused view's own
 * realtime hook) — the legacy `TysonCoachingContext` is no longer involved.
 *
 * Bootstrap policy:
 *   1. Wait for `companyId` to land.
 *   2. Look up `companyId → conversationId` in localStorage. If present,
 *      `resume()` the existing conversation and skip the create POST.
 *   3. Otherwise `createNew({ companyId, ... })`, cache the new id keyed by
 *      `companyId` so the next reload resumes.
 *   4. On 4xx during history backfill (stale cache), the caller should clear
 *      the cache entry and retry — handled by `useRealtimeCoaching`'s
 *      `historyQuery` failing silently; future iteration: surface this here.
 *
 * Send-queue gate and tag-claim semantics are unchanged from the prior
 * `useTysonCoaching`-backed implementation — see pins/README.md "End-to-end
 * flow" for the full picture.
 */
export function useCoaching(args: UseCoachingArgs): UseCoachingApi {
	// Pass `companyId` so the realtime hook attaches `SalesLeadAIContext` on
	// every outbound stream — without this the AI doesn't see lead data even
	// though the conversation was created with `companyId`.
	const realtime = useRealtimeCoaching({ companyId: args.companyId });

	// Each queued tag carries `skipIds`: the set of assistant-message ids that were
	// already in upstream at the moment the user clicked the keyword pin. Those
	// pre-existing messages are not the reply to this send and must not be claimed,
	// even if they happen to be untagged. See pins/README.md "Why snapshots".
	type QueuedTag = PendingKeywordTag & { skipIds: Set<string> };
	type QueueItem = { kind: 'direct'; text: string; tag?: QueuedTag } | { kind: 'autoSuggestion' } | { kind: 'summary' };
	const pendingTagsRef = React.useRef<QueuedTag[]>([]);
	const tagByIdRef = React.useRef<Map<string, PendingKeywordTag>>(new Map());
	const sendQueueRef = React.useRef<QueueItem[]>([]);
	const inFlightRef = React.useRef(false);
	const upstreamRef = React.useRef(realtime.coachingMessages);
	const [tagVersion, setTagVersion] = React.useState(0);
	// Track which (companyId, contactId) pair we've already auto-bootstrapped
	// for so we don't fire two conversations on re-renders.
	const startedKeyRef = React.useRef<string | null>(null);
	// Tracks the last `companyId` we bootstrapped for. When the rep switches
	// leads (Search → click result, Next lead, inbound CallFromUnknown
	// attach), this lets the bootstrap effect detect the company change and
	// reset the conversation — otherwise the `realtime.conversationId` guard
	// below would keep the panel pinned to the previous lead's conversation.
	const lastCompanyIdRef = React.useRef<string | null>(null);

	const upstream = realtime.coachingMessages;
	upstreamRef.current = upstream;

	// Drop the send + tag queues and release the in-flight lock. Used both by
	// `startNewConversation` (explicit "start fresh") and the lead-switch branch
	// of the bootstrap effect below. Without this on lead switch, a send or
	// post-call summary queued / in flight on the previous lead drains into the
	// next lead's freshly-created conversation, and a pending keyword tag claims
	// the next lead's first assistant message.
	const clearSendAndTagQueues = React.useCallback(() => {
		sendQueueRef.current = [];
		pendingTagsRef.current = [];
		tagByIdRef.current = new Map();
		inFlightRef.current = false;
	}, []);

	// Auto-bootstrap. Resume an existing conversation for this companyId if
	// one is cached; otherwise create a new one and cache the id for next
	// reload.
	React.useEffect(() => {
		const { companyId, contactId, phoneCallId, leadServedId, agentTemplateId } = args;
		if (!companyId) return;

		// Lead switched since the last bootstrap. Reset realtime state so the
		// new lead's cached / freshly-created conversation doesn't inherit
		// messages from the prior lead, then fall through to resume/createNew
		// in the same effect run. Returning early here (the prior behaviour)
		// left a render gap where the old conversation was still active.
		const isLeadSwitch = !!lastCompanyIdRef.current && lastCompanyIdRef.current !== companyId;
		if (isLeadSwitch) {
			realtime.reset();
			// Same teardown as `startNewConversation` — otherwise a queued / in-
			// flight send or post-call summary from the prior lead drains into
			// this lead's conversation once the old stream ends.
			clearSendAndTagQueues();
			startedKeyRef.current = null;
		}

		lastCompanyIdRef.current = companyId;

		// When the bootstrap has already produced a conversation for *this*
		// lead, nothing to do. We deliberately skip this guard on lead-switch
		// because `realtime.reset()` above only schedules state changes — the
		// rendered `realtime.conversationId` still reflects the prior lead
		// until the next commit.
		if (!isLeadSwitch && (realtime.conversationId || realtime.isCreatingConversation)) return;

		// Dedup key intentionally omits `agentTemplateId` — switching templates
		// mid-session does NOT force a new conversation. The new template id only
		// takes effect when `startNewConversation` (or a lead switch) bootstraps
		// a fresh conversation. Matches legacy `useTysonRealtimeCoaching`.
		const key = `${companyId}:${contactId ?? '-'}`;
		if (startedKeyRef.current === key) return;
		startedKeyRef.current = key;

		const cached = getCachedConversationId(companyId);
		if (cached) {
			realtime.resume(cached);
			return;
		}
		void realtime.createNew({ companyId, phoneCallId, leadServedId, agentTemplateId }).then(id => {
			if (id) setCachedConversationId(companyId, id);
		});
	}, [args, realtime, clearSendAndTagQueues]);

	// Auto-enrich the existing conversation with the active `phoneCallId` when
	// a real call goes live. Mirrors legacy `useRealCallCoachingSession`
	// (lines 38-45). The bootstrap above only carries the phoneCallId on the
	// FIRST `createNew` — if coaching was created before the call started (or
	// resumed from cache), the conversation has no phoneCallId until this
	// effect stitches it in. A ref dedupes against the same id so a flurry of
	// re-renders during the call only enriches once.
	const lastEnrichedPhoneCallIdRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		const { phoneCallId } = args;
		if (!phoneCallId) return;
		if (!realtime.conversationId) return;
		if (lastEnrichedPhoneCallIdRef.current === phoneCallId) return;
		lastEnrichedPhoneCallIdRef.current = phoneCallId;
		realtime.enrichCreationContext({ phoneCallId });
	}, [args, realtime]);

	// Auto-enrich the conversation with `leadServedId` once it's known. The
	// bootstrap above usually fires before the lead query resolves (`companyId`
	// arrives from the URL / next-lead response first), so the create POST
	// rarely carries the id — this effect stitches it in afterwards, restoring
	// the legacy queue's behavior where `startCoachingBeforeCall` always put
	// `leadServedId` on the creation context. The dedup key includes the
	// conversationId (unlike the phoneCallId enrich above) because lead
	// switches ALWAYS change `leadServedId` while the old conversation is still
	// rendered — keying on the pair ensures the enrich re-fires once the NEW
	// conversation lands instead of being swallowed by the dedup.
	const lastEnrichedLeadServedKeyRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		const { leadServedId } = args;
		if (!leadServedId) return;
		if (!realtime.conversationId) return;
		const key = `${realtime.conversationId}:${leadServedId}`;
		if (lastEnrichedLeadServedKeyRef.current === key) return;
		lastEnrichedLeadServedKeyRef.current = key;
		realtime.enrichCreationContext({ leadServedId });
	}, [args, realtime]);

	// Tag-claim effect — see pins/README.md → "Why snapshots" for the full
	// rationale on the `skipIds` filter.
	React.useEffect(() => {
		if (pendingTagsRef.current.length === 0) return;

		let claimed = false;
		for (const msg of upstream) {
			if (pendingTagsRef.current.length === 0) break;
			if (msg.role !== 'assistant') continue;
			if (tagByIdRef.current.has(msg.id)) continue;
			const head = pendingTagsRef.current[0];
			if (head.skipIds.has(msg.id)) continue;
			pendingTagsRef.current.shift();
			tagByIdRef.current.set(msg.id, { pinnedKeyword: head.pinnedKeyword, pinColor: head.pinColor });
			claimed = true;
		}
		if (claimed) setTagVersion(v => v + 1);
	}, [upstream]);

	const messages = React.useMemo<CoachMessage[]>(
		() => upstream.map(m => toCoachMessage(m, tagByIdRef.current)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[upstream, tagVersion]
	);

	// Pull the next queued send and dispatch it, if nothing is currently in flight.
	// Auto-suggestion items can be no-ops if no transcript has accumulated; in
	// that case we release the lock immediately and try the next item rather
	// than wait for a stream-end transition that never comes.
	const drainSendQueue = React.useCallback(() => {
		if (inFlightRef.current) return;
		const next = sendQueueRef.current.shift();
		if (!next) return;
		inFlightRef.current = true;
		if (next.kind === 'direct') {
			if (next.tag) pendingTagsRef.current.push(next.tag);
			realtime.sendDirectQuestion(next.text);
			return;
		}
		if (next.kind === 'summary') {
			realtime.requestPostCallSummary();
			return;
		}
		// kind === 'autoSuggestion'
		const flushed = realtime.sendTranscriptSuggestion();
		if (flushed === 0) {
			inFlightRef.current = false;
			drainSendQueue();
		}
	}, [realtime]);

	// Watch the AI-responding flag. A true→false transition releases the
	// in-flight lock and dispatches the next queued send.
	//
	// On a lead switch the bootstrap effect above aborts the prior lead's stream,
	// which flips this flag true→false and fires this drain. That's safe because
	// the bootstrap effect (declared earlier, so it runs first) has already
	// emptied the send queue via `clearSendAndTagQueues()` — so the drain finds
	// nothing to dispatch into the new lead. Keep that ordering: this watcher
	// must stay declared after the bootstrap effect.
	const wasRespondingRef = React.useRef(false);
	React.useEffect(() => {
		const isResponding = realtime.isAIResponding;
		if (wasRespondingRef.current && !isResponding) {
			inFlightRef.current = false;
			drainSendQueue();
		}
		wasRespondingRef.current = isResponding;
	}, [realtime.isAIResponding, drainSendQueue]);

	const send = React.useCallback(
		(text: string, tag?: PendingKeywordTag) => {
			const trimmed = text.trim();
			if (!trimmed) return;
			let queuedTag: QueuedTag | undefined;
			if (tag) {
				const skipIds = new Set<string>();
				for (const m of upstreamRef.current) {
					if (m.role === 'assistant') skipIds.add(m.id);
				}
				queuedTag = { ...tag, skipIds };
			}
			sendQueueRef.current.push({ kind: 'direct', text: trimmed, tag: queuedTag });
			drainSendQueue();
		},
		[drainSendQueue]
	);

	const requestAutoSuggestion = React.useCallback(() => {
		// At most one suggestion can be queued at a time. If something else is
		// already queued or in flight (a direct send, or another suggestion),
		// the lead's continued speech keeps appending to `pendingTranscriptRef`
		// inside the realtime hook — when the queued suggestion finally fires,
		// it batches the entire accumulated transcript in one request rather
		// than triggering a second AI reply per utterance.
		const hasPendingSuggestion = sendQueueRef.current.some(item => item.kind === 'autoSuggestion');
		if (hasPendingSuggestion) return;
		sendQueueRef.current.push({ kind: 'autoSuggestion' });
		drainSendQueue();
	}, [drainSendQueue]);

	const startNewConversation = React.useCallback(() => {
		const { companyId } = args;
		if (companyId) clearCachedConversationId(companyId);
		// Drop send + tag queues so a stale before-call burst from the previous
		// conversation doesn't carry over into the fresh one.
		clearSendAndTagQueues();
		// Force the auto-bootstrap effect to run again.
		startedKeyRef.current = null;
		realtime.reset();
	}, [args, realtime, clearSendAndTagQueues]);

	// Internal helper: queue a post-call summary unless one is already queued
	// or in flight. Dedup so a flurry of clicks / repeated `call:ended` events
	// from Twilio (reconnects, glitches) only summarizes once.
	const queueSummary = React.useCallback(() => {
		const hasPendingSummary = sendQueueRef.current.some(item => item.kind === 'summary');
		if (hasPendingSummary) return;
		sendQueueRef.current.push({ kind: 'summary' });
		drainSendQueue();
	}, [drainSendQueue]);

	const endCall = React.useCallback(() => {
		// Only request the post-call summary if real transcription happened.
		// Without this guard, dial failures (e.g. Twilio gateway 31005 immediately
		// after `device.connect()`) trigger `call:ended` while the auto-Game-Plan
		// is still streaming, and the summary POST collides with the open stream
		// for a 409.
		const hasTranscript = upstreamRef.current.some(m => m.messageType === 'transcript');
		if (!hasTranscript) return;
		// Route through the send queue so the summary waits for any in-flight
		// stream (Game Plan, last auto-suggestion, etc.) to finish — Anthropic
		// 409s a concurrent POST to the same conversation, and the AI provider
		// 429s burst writes.
		queueSummary();
	}, [queueSummary]);

	// Public manual trigger for "Get summary". Skips the transcript guard
	// because the rep has explicitly clicked the button — we trust their
	// intent even if the transcript pipeline missed entries.
	const requestPostCallSummary = React.useCallback(() => {
		queueSummary();
	}, [queueSummary]);

	return {
		messages,
		isResponding: realtime.isAIResponding,
		isLoadingHistory: realtime.isLoadingHistory,
		wasResumed: realtime.wasResumed,
		conversationId: realtime.conversationId,
		isCreatingConversation: realtime.isCreatingConversation,
		send,
		addTranscriptEntry: realtime.addTranscriptEntry,
		requestAutoSuggestion,
		requestSuggestion: requestAutoSuggestion,
		requestPostCallSummary,
		startNewConversation,
		endCall,
	};
}
